import { FC, useMemo } from 'react'

import { StatusChip, StatusChipSizes } from '@inno-ui/status-chip'
import { Tooltip, TooltipPlacements } from '@inno-ui/tooltip'
// imported from the specific file, not the stores barrel — the barrel
// pulls in restorableLocalWindows.tsx, which imports WidgetMarketplace,
// which imports this same widgets module, creating a circular require.
import { useDesktopWidgets } from 'modules/desktop/modules/widgets/hooks'
import { useTranslation } from 'react-i18next'
import { WidgetKey } from 'shared/constants/widgets'

import { MarketplaceTileShell } from '../../../MarketplaceTileShell'
import { ViewportMount } from '../../../ViewportMount/ViewportMount'
import { WidgetWrapper } from '../../../WidgetWrapper'
import { REVIEW_STATUS_COLORS } from '../../reviewStatusColors'
import { resolveArtifactSize } from '../../utils/resolveArtifactSize'
import { ArtifactFrame } from '../ArtifactFrame'
import styles from './AiArtifactMarketplaceItems.module.scss'
import { AiArtifactMarketplaceItemsProps } from './AiArtifactMarketplaceItems.types'
import { getPinnedPublicIds } from './aiArtifactMarketplace.utils'

export const AiArtifactMarketplaceItems: FC<
  AiArtifactMarketplaceItemsProps
> = ({
  onCardDragStart,
  onCardDragEnd,
  artifacts,
  draggingPublicId,
  onCardTouchStart,
  onCardTouchMove,
  onCardTouchEnd
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })

  const { items: sidebarItems } = useDesktopWidgets()
  const pinnedPublicIds = useMemo(
    () => getPinnedPublicIds(sidebarItems),
    [sidebarItems]
  )

  return (
    <div className={styles.previews}>
      {artifacts.map(artifact => {
        const inUse = pinnedPublicIds.has(artifact.public_id)
        const shared = !artifact.mine
        // owner-only review status (recipients never see a non-approved widget)
        const ownPending = artifact.mine && artifact.review_status === 'pending'
        const ownDeclined =
          artifact.mine && artifact.review_status === 'declined'
        const size = resolveArtifactSize(artifact.default_size)
        // Both inputs stay wired: touch events only fire for touch, and the
        // native drag guard in the marketplace drops the duplicate if a
        // touchscreen promotes the long press to a drag.
        const canDrag = !inUse
        const isDraggingCard = draggingPublicId === artifact.public_id

        return (
          <MarketplaceTileShell
            key={artifact.public_id}
            inUse={inUse}
            isDragging={isDraggingCard}
            name={
              <div className={styles.caption}>
                <div className={styles.nameRow}>
                  <span className={styles.nameText}>
                    {artifact.title} {size}
                  </span>
                  <Tooltip
                    overlay={t('aiGenerated')}
                    placement={TooltipPlacements.Top}
                  >
                    <span
                      className={styles.aiIcon}
                      role='img'
                      aria-label={t('aiGenerated')}
                    />
                  </Tooltip>
                </div>
                {/* creator name on shared tiles (falls back to a generic pill) */}
                {shared &&
                  (artifact.owner_name ? (
                    <Tooltip
                      overlay={t('sharedBy', { name: artifact.owner_name })}
                      placement={TooltipPlacements.Bottom}
                    >
                      <span className={styles.creator}>
                        {t('created_by', { name: artifact.owner_name })}
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      overlay={t('sharedWithYou')}
                      placement={TooltipPlacements.Bottom}
                    >
                      <span className={styles.sharedFlag}>{t('shared')}</span>
                    </Tooltip>
                  ))}
                {/* owner-only: this widget is gated until a reviewer acts.
                    Same tooltip pattern as declined so pending/declined have
                    parity when hovered. */}
                {ownPending && (
                  <Tooltip
                    overlay={t('status_pending_hint')}
                    placement={TooltipPlacements.Bottom}
                  >
                    <StatusChip
                      label={t('status_pending')}
                      color={REVIEW_STATUS_COLORS.pending}
                      size={StatusChipSizes.XXS}
                      fixedWidth={false}
                    />
                  </Tooltip>
                )}
                {ownDeclined && (
                  <Tooltip
                    overlay={
                      artifact.review_reason
                        ? t('status_declined_reason', {
                            reason: artifact.review_reason
                          })
                        : t('status_declined_no_reason')
                    }
                    placement={TooltipPlacements.Bottom}
                  >
                    <StatusChip
                      label={t('status_declined')}
                      color={REVIEW_STATUS_COLORS.declined}
                      size={StatusChipSizes.XXS}
                      fixedWidth={false}
                    />
                  </Tooltip>
                )}
              </div>
            }
            title={
              inUse
                ? t('alreadyOnDesktop', { title: artifact.title })
                : t('dragToAdd', { title: artifact.title })
            }
            onTouchStart={
              !inUse
                ? onCardTouchStart(artifact.public_id, size, artifact.title)
                : undefined
            }
            onTouchMove={isDraggingCard ? onCardTouchMove : undefined}
            onTouchEnd={isDraggingCard ? onCardTouchEnd : undefined}
            onTouchCancel={isDraggingCard ? onCardTouchEnd : undefined}
          >
            <WidgetWrapper
              widget={{ widgetKey: WidgetKey.AI_ARTIFACT_TILE, size }}
              isDragging={isDraggingCard}
              showInUseOverlay={inUse}
              disableContextMenu
              fillMode
            >
              {/* pointer-events:none, cross-origin iframe would swallow drag events */}
              <ViewportMount className={styles.artifactPreview}>
                <ArtifactFrame
                  publicId={artifact.public_id}
                  view='mini'
                  isInFullView={false}
                  hideBar
                />
              </ViewportMount>
            </WidgetWrapper>
            {canDrag && (
              <div
                className={styles.dragSurface}
                draggable
                onDragStart={onCardDragStart(
                  artifact.public_id,
                  size,
                  artifact.title
                )}
                onDragEnd={onCardDragEnd}
                aria-hidden='true'
              />
            )}
          </MarketplaceTileShell>
        )
      })}
    </div>
  )
}
